import AddRounded from '@mui/icons-material/AddRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import EventRepeatRounded from '@mui/icons-material/EventRepeatRounded';
import LayersRounded from '@mui/icons-material/LayersRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { Money } from '../../components/Money';
import { EmptyState, ErrorState, PageHeader } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { Segmented, StatGrid, StatTile } from '../../components/ui';
import { count, dateTime, plural, relativeTime } from '../../lib/format';
import { INTERVAL_LABELS, PlanDialog, type Plan } from './PlanDialog';

type Filter = 'all' | 'active' | 'inactive';

export function PlansPage() {
  const { can } = useAuth();
  const manage = can('plans.manage');
  const [filter, setFilter] = useState<Filter>('all');
  const [editing, setEditing] = useState<Plan | null>(null);
  const [creating, setCreating] = useState(false);
  const query = useQuery({
    queryKey: ['plans'],
    queryFn: () => unwrap(api.GET('/platform/plans')),
  });
  const plans = query.data?.data ?? [];
  const shown = plans.filter((p) =>
    filter === 'all' ? true : filter === 'active' ? p.isActive : !p.isActive,
  );
  const active = plans.filter((p) => p.isActive).length;

  return (
    <Box>
      <PageHeader
        eyebrow="Customers"
        title="Plans"
        subtitle="Subscription plans set the duration and list price used when a shop is extended."
        actions={
          manage && (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => setCreating(true)}
            >
              New plan
            </Button>
          )
        }
      />

      {query.isError ? (
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      ) : (
        <>
          <StatGrid>
            <StatTile
              label="Plans"
              value={count(plans.length)}
              loading={query.isPending}
              icon={<LayersRounded />}
            />
            <StatTile
              label="Active"
              value={count(active)}
              hint="Selectable for extensions"
              tone="success"
              loading={query.isPending}
            />
            <StatTile
              label="Inactive"
              value={count(plans.length - active)}
              tone="neutral"
              loading={query.isPending}
            />
          </StatGrid>

          <Stack
            direction="row"
            sx={{ mb: 2, alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
          >
            <Segmented
              label="Show plans"
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
            {!query.isPending && (
              <Typography variant="body2" color="text.secondary">
                {plural(shown.length, 'plan')}
              </Typography>
            )}
          </Stack>

          {query.isPending ? (
            <PlanGrid>
              {Array.from({ length: 3 }, (_, i) => (
                <Card key={i}>
                  <CardContent>
                    <Skeleton width="50%" height={28} />
                    <Skeleton width="30%" />
                    <Skeleton width="70%" height={44} sx={{ mt: 2 }} />
                    <Skeleton width="60%" />
                  </CardContent>
                </Card>
              ))}
            </PlanGrid>
          ) : shown.length === 0 ? (
            <Card>
              <EmptyState
                icon={<LayersRounded />}
                title={plans.length === 0 ? 'No plans yet' : 'No plans match'}
                body={
                  plans.length === 0
                    ? 'Create a plan so billing staff can extend shops by a standard period and price.'
                    : 'Try another filter.'
                }
                action={
                  plans.length === 0 && manage
                    ? { label: 'New plan', onClick: () => setCreating(true) }
                    : undefined
                }
              />
            </Card>
          ) : (
            <PlanGrid>
              {shown.map((p) => (
                <PlanCard key={p.id} plan={p} onEdit={manage ? () => setEditing(p) : undefined} />
              ))}
            </PlanGrid>
          )}
        </>
      )}

      <PlanDialog open={creating} onClose={() => setCreating(false)} />
      <PlanDialog open={editing !== null} plan={editing} onClose={() => setEditing(null)} />
    </Box>
  );
}

function PlanGrid({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
      }}
    >
      {children}
    </Box>
  );
}

function PlanCard({ plan, onEdit }: { plan: Plan; onEdit?: () => void }) {
  return (
    <Card
      component="article"
      aria-label={`Plan ${plan.name}`}
      sx={(t) => ({
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        opacity: plan.isActive ? 1 : 0.82,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '0 0 auto 0',
          height: 3,
          background: plan.isActive
            ? `linear-gradient(90deg, ${(t.vars ?? t).palette.primary.main}, color-mix(in srgb, ${(t.vars ?? t).palette.primary.main} 30%, transparent))`
            : (t.vars ?? t).palette.divider,
        },
      })}
    >
      <CardContent sx={{ flex: 1 }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" component="h2" sx={{ wordBreak: 'break-word' }}>
              {plan.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}
            >
              {plan.code}
            </Typography>
          </Box>
          <StatusChip
            status={plan.isActive ? 'ACTIVE' : 'INACTIVE'}
            label={plan.isActive ? 'Active' : 'Inactive'}
          />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mt: 2.5 }}>
          <Typography
            component="div"
            className="tabular"
            sx={{ fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1 }}
          >
            {plan.price === null ? (
              'No list price'
            ) : (
              <Money value={plan.price} currency={plan.currency} />
            )}
          </Typography>
          {plan.price !== null && (
            <Typography variant="body2" color="text.secondary">
              / {plural(plan.durationDays, 'day')}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Chip
            size="small"
            variant="outlined"
            icon={<EventRepeatRounded />}
            label={INTERVAL_LABELS[plan.interval]}
          />
          <Chip size="small" variant="outlined" label={plural(plan.durationDays, 'day')} />
          <Chip
            size="small"
            variant="outlined"
            label={
              plan.maxUsers === null ? 'Unlimited users' : `Up to ${plural(plan.maxUsers, 'user')}`
            }
          />
          <Chip size="small" variant="outlined" label={plan.currency} />
        </Stack>
      </CardContent>
      <Divider />
      <Stack
        direction="row"
        sx={{ px: 2.5, py: 1.25, alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Tooltip title={`Updated ${dateTime(plan.updatedAt)} · version ${String(plan.version)}`}>
          <Typography variant="caption" color="text.secondary">
            Updated {relativeTime(plan.updatedAt)}
          </Typography>
        </Tooltip>
        {onEdit && (
          <Button
            size="small"
            startIcon={<EditRounded />}
            onClick={onEdit}
            aria-label={`Edit ${plan.name}`}
          >
            Edit
          </Button>
        )}
      </Stack>
    </Card>
  );
}
